/**
 * Comercial → Prospecção (issues #115/#122).
 *
 * Pesquisa na base pública de CNPJ (Receita Federal, alimentada pelo
 * cnpj-data-pipeline numa VPS separada — GET /api/empresas/publicas) e
 * import das selecionadas pra dentro da organização (POST
 * /api/empresas/importar, grava em `crm.companies` + satélites). As
 * importadas aparecem em Comercial → Empresas.
 *
 * Layout segue o design de referência `prospeccao-titam.html` (raiz do repo):
 * busca principal (Nome fantasia | CNPJ), filtros agrupados em Localização /
 * Atividade / Perfil da empresa, chips dos filtros ativos no rodapé do painel,
 * estado vazio com atalhos e tabela de resultados com import por linha.
 * Estilos em prospeccao.css (escopo `.pp`).
 *
 * Diferenças do design, por requisito da issue #122: Porte aceita vários
 * valores (menu com checkboxes) e inclui MEI.
 *
 * Degrada com aviso (não erro) quando a base pública está fora do ar: a API
 * responde 503 e a tela mostra a mensagem em vez de uma tela de erro genérica.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { getCompanies, importarEmpresas, searchEmpresasPublicas } from '../../db/api.js'
import DealModal from './DealModal.jsx'
import { fmtCnpj, SITUACOES } from './Empresas.jsx'
import './prospeccao.css'

const PAGE_SIZE = 20

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

const PORTES = [
  { value: 'mei', label: 'MEI' },
  { value: 'me', label: 'ME' },
  { value: 'epp', label: 'EPP' },
  { value: 'demais', label: 'Demais' },
]
const PORTE_LABEL = Object.fromEntries(PORTES.map((p) => [p.value, p.label]))
const SITUACAO_LABEL = Object.fromEntries(SITUACOES.map((s) => [s.value, s.label]))

const EMPTY_FILTERS = {
  modo: 'nome', q: '', uf: '', cidade: '', cnae: '', cnae_secundarios: false,
  situacao_cadastral: '', porte: [], data_abertura_de: '', data_abertura_ate: '',
  opcao_simples: false,
}

const digits = (v) => String(v || '').replace(/\D/g, '')
const maskCnpj = (v) => digits(v).slice(0, 14)
  .replace(/^(\d{2})(\d)/, '$1.$2')
  .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
  .replace(/\.(\d{3})(\d)/, '.$1/$2')
  .replace(/(\d{4})(\d)/, '$1-$2')
const maskCnae = (v) => digits(v).slice(0, 7)
  .replace(/^(\d{4})(\d)/, '$1-$2')
  .replace(/^(\d{4}-\d)(\d)/, '$1/$2')
const fmtDate = (iso) => {
  if (!iso) return '—'
  const [y, m, d] = String(iso).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}
const isoDaysAgo = (days) => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10)

// Filtros da tela → parâmetros da API (GET /empresas/publicas).
const toApiFilters = (f) => ({
  nome_fantasia: f.modo === 'nome' ? f.q.trim() : '',
  cnpj: f.modo === 'cnpj' ? digits(f.q) : '',
  uf: f.uf,
  cidade: f.uf ? f.cidade.trim() : '',
  cnae: digits(f.cnae),
  cnae_secundarios: f.cnae_secundarios && !!digits(f.cnae),
  situacao_cadastral: f.situacao_cadastral,
  porte: f.porte,
  opcao_simples: f.opcao_simples,
  data_abertura_de: f.data_abertura_de,
  data_abertura_ate: f.data_abertura_ate,
})

// Chips do rodapé: um por filtro ativo, cada um sabe se limpar.
const activeChips = (f) => {
  const chips = []
  if (f.q.trim()) chips.push({ key: 'q', label: `${f.modo === 'cnpj' ? 'CNPJ' : 'Nome'}: ${f.q.trim()}`, clear: { q: '' } })
  if (f.uf) chips.push({ key: 'uf', label: `UF: ${f.uf}`, clear: { uf: '', cidade: '' } })
  if (f.uf && f.cidade.trim()) chips.push({ key: 'cidade', label: `Cidade: ${f.cidade.trim()}`, clear: { cidade: '' } })
  if (digits(f.cnae)) chips.push({ key: 'cnae', label: `CNAE: ${f.cnae}${f.cnae_secundarios ? ' + secundários' : ''}`, clear: { cnae: '', cnae_secundarios: false } })
  if (f.situacao_cadastral) chips.push({ key: 'sit', label: `Situação: ${SITUACAO_LABEL[f.situacao_cadastral]}`, clear: { situacao_cadastral: '' } })
  if (f.porte.length) chips.push({ key: 'porte', label: `Porte: ${f.porte.map((p) => PORTE_LABEL[p]).join(', ')}`, clear: { porte: [] } })
  if (f.data_abertura_de) chips.push({ key: 'de', label: `Aberta desde ${fmtDate(f.data_abertura_de)}`, clear: { data_abertura_de: '' } })
  if (f.data_abertura_ate) chips.push({ key: 'ate', label: `Aberta até ${fmtDate(f.data_abertura_ate)}`, clear: { data_abertura_ate: '' } })
  if (f.opcao_simples) chips.push({ key: 'simples', label: 'Optante do Simples', clear: { opcao_simples: false } })
  return chips
}

function PorteSelect({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const root = useRef(null)

  useEffect(() => {
    if (!open) return
    const outside = (e) => { if (!root.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [open])

  const toggle = (v) => onChange(value.includes(v) ? value.filter((p) => p !== v) : [...value, v])
  const label = value.length ? PORTES.filter((p) => value.includes(p.value)).map((p) => p.label).join(', ') : 'Todos'

  return (
    <div className="pp-multi" ref={root} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}>
      <button type="button" id="pp-porte" className="pp-ctl pp-select" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>{label}</span>
      </button>
      {open && (
        <div className="pp-multi-menu" role="group" aria-label="Porte">
          {PORTES.map((p) => (
            <label key={p.value} className="pp-check">
              <input type="checkbox" checked={value.includes(p.value)} onChange={() => toggle(p.value)} />
              {p.label}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Prospeccao() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [unavailable, setUnavailable] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [importing, setImporting] = useState(new Set())
  const [importMsg, setImportMsg] = useState('')
  const [importOk, setImportOk] = useState(false)
  const [dealCompany, setDealCompany] = useState(null)
  const [openingDeal, setOpeningDeal] = useState('')
  const openingDealRef = useRef(false)

  const patch = (changes) => setFilters((f) => ({ ...f, ...changes }))
  const setField = (key) => (e) => patch({ [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })
  const chips = activeChips(filters)

  const buscar = useCallback(async (targetPage, f = filters) => {
    if (!activeChips(f).length) { setErr('Informe ao menos um filtro de busca.'); return }
    if (f.modo === 'nome' && f.q.trim() && f.q.trim().length < 3) { setErr('Nome fantasia: informe pelo menos 3 letras.'); return }
    setErr(''); setImportMsg(''); setUnavailable(false); setLoading(true)
    try {
      const data = await searchEmpresasPublicas({ ...toApiFilters(f), page: targetPage, page_size: PAGE_SIZE })
      setResult(data)
      setPage(targetPage)
      setSelected(new Set())
    } catch (e) {
      setResult(null)
      if (e.status === 503) setUnavailable(true)
      else setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [filters])

  const limpar = () => { setFilters(EMPTY_FILTERS); setResult(null); setErr(''); setImportMsg('') }

  const atalho = (changes) => {
    const next = { ...EMPTY_FILTERS, ...changes }
    setFilters(next)
    buscar(1, next)
  }

  const items = result?.items || []
  const totalPages = result ? Math.max(1, Math.ceil(result.total / PAGE_SIZE)) : 1
  const importaveis = items.filter((it) => !it.already_imported).map((it) => it.cnpj)
  const todasSelecionadas = importaveis.length > 0 && importaveis.every((c) => selected.has(c))

  const toggle = (cnpj) => setSelected((s) => {
    const next = new Set(s)
    if (next.has(cnpj)) next.delete(cnpj); else next.add(cnpj)
    return next
  })
  const toggleTodas = () => setSelected(todasSelecionadas ? new Set() : new Set(importaveis))

  const criarNegocio = async (item) => {
    if (openingDealRef.current) return
    openingDealRef.current = true
    setOpeningDeal(item.cnpj); setErr('')
    try {
      // Resolve a empresa do CRM pelo CNPJ; o resultado público não tem o ID do CRM.
      const cnpj = String(item.cnpj).replace(/\D/g, '')
      const companies = await getCompanies(cnpj)
      const company = companies.find(row => String(row.cnpj || '').replace(/\D/g, '') === cnpj)
      if (!company) throw new Error('Empresa importada não encontrada no CRM. Atualize a busca e tente novamente.')
      setDealCompany(company)
    } catch (error) { setErr(error.message) }
    finally { openingDealRef.current = false; setOpeningDeal('') }
  }

  const importar = async (cnpjs) => {
    if (!cnpjs.length) return
    setImporting((s) => new Set([...s, ...cnpjs]))
    setImportMsg('')
    try {
      const { imported, failed } = await importarEmpresas(cnpjs)
      const ok = new Set(imported.map((c) => c.cnpj))
      setImportOk(imported.length > 0)
      setImportMsg(
        `${imported.length} empresa(s) importada(s) para a organização`
        + (failed.length ? `; ${failed.length} não encontrada(s) na base pública.` : '.')
      )
      setResult((r) => r && { ...r, items: r.items.map((it) => (ok.has(it.cnpj) ? { ...it, already_imported: true } : it)) })
      setSelected((s) => new Set([...s].filter((c) => !ok.has(c))))
    } catch (e) {
      setImportOk(false)
      setImportMsg(e.status === 503 ? 'Base pública ficou indisponível durante o import.' : e.message)
    } finally {
      setImporting((s) => new Set([...s].filter((c) => !cnpjs.includes(c))))
    }
  }

  const resultTitle = result
    ? result.total_capped
      ? `Mais de ${result.total.toLocaleString('pt-BR')} empresas encontradas`
      : `${result.total.toLocaleString('pt-BR')} ${result.total === 1 ? 'empresa encontrada' : 'empresas encontradas'}`
    : ''

  return (
    <Layout title="Prospecção" breadcrumb={[{ label: 'Comercial' }, { label: 'Prospecção' }]}>
      <div className="pp">
        <h1 className="pp-title">Prospecção</h1>
        <p className="pp-lede">
          Pesquise empresas na base pública de CNPJ da Receita Federal e importe as que
          interessam à sua organização. Depois de importadas, os dados passam a ser do CRM
          e podem ser editados normalmente. A base pública nunca é alterada.
        </p>

        {unavailable && <div className="pp-alert">Base pública de CNPJ indisponível no momento. Tente novamente mais tarde.</div>}
        {err && <div className="pp-alert">{err}</div>}
        {importMsg && (
          <div className="pp-notice">
            {importMsg}
            {importOk && <>{' '}<button type="button" className="pp-link" onClick={() => navigate('/comercial/empresas')}>Ver em Empresas</button></>}
          </div>
        )}

        <form className="pp-panel" onSubmit={(e) => { e.preventDefault(); buscar(1) }} noValidate>
          <div className="pp-panel-head">
            <fieldset className="pp-seg">
              <legend>Buscar por</legend>
              {[['nome', 'Nome fantasia'], ['cnpj', 'CNPJ']].map(([value, label]) => (
                <label key={value}>
                  <input type="radio" name="pp-modo" value={value} checked={filters.modo === value} onChange={() => patch({ modo: value, q: '' })} />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>
            <div className="pp-bigsearch">
              <Icon n="search" s={22} />
              <input
                type="text" autoComplete="off" aria-label="Termo de busca"
                inputMode={filters.modo === 'cnpj' ? 'numeric' : 'text'}
                placeholder={filters.modo === 'cnpj' ? '00.000.000/0000-00' : 'Digite o nome fantasia da empresa'}
                value={filters.q}
                onChange={(e) => patch({ q: filters.modo === 'cnpj' ? maskCnpj(e.target.value) : e.target.value })}
              />
            </div>
          </div>

          <div className="pp-frow">
            <div><h2>Localização</h2><p>Restrinja por estado e cidade.</p></div>
            <div className="pp-fields">
              <div className="pp-field">
                <label className="pp-lbl" htmlFor="pp-uf">UF</label>
                <select className="pp-ctl pp-select" id="pp-uf" value={filters.uf} onChange={(e) => patch({ uf: e.target.value, ...(e.target.value ? {} : { cidade: '' }) })}>
                  <option value="">Todos</option>
                  {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
              <div className="pp-field pp-span2">
                <label className="pp-lbl" htmlFor="pp-cidade">Cidade</label>
                <input
                  className="pp-ctl" id="pp-cidade" autoComplete="off" disabled={!filters.uf}
                  placeholder={filters.uf ? 'Digite a cidade' : 'Escolha uma UF primeiro'}
                  value={filters.cidade} onChange={setField('cidade')}
                />
              </div>
            </div>
          </div>

          <div className="pp-frow">
            <div><h2>Atividade</h2><p>Classificação Nacional de Atividades Econômicas.</p></div>
            <div className="pp-fields">
              <div className="pp-field pp-span2">
                <label className="pp-lbl" htmlFor="pp-cnae">CNAE</label>
                <input
                  className="pp-ctl" id="pp-cnae" inputMode="numeric" placeholder="0000-0/00" maxLength={9} autoComplete="off"
                  value={filters.cnae} onChange={(e) => patch({ cnae: maskCnae(e.target.value) })}
                />
                <label className="pp-check pp-check-below">
                  <input type="checkbox" checked={filters.cnae_secundarios} onChange={setField('cnae_secundarios')} />
                  Incluir CNAEs secundários
                </label>
              </div>
            </div>
          </div>

          <div className="pp-frow">
            <div><h2>Perfil da empresa</h2><p>Situação cadastral, porte e data de abertura.</p></div>
            <div className="pp-fields">
              <div className="pp-field">
                <label className="pp-lbl" htmlFor="pp-situacao">Situação</label>
                <select className="pp-ctl pp-select" id="pp-situacao" value={filters.situacao_cadastral} onChange={setField('situacao_cadastral')}>
                  <option value="">Todas</option>
                  {SITUACOES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="pp-field">
                <label className="pp-lbl" htmlFor="pp-porte">Porte</label>
                <PorteSelect value={filters.porte} onChange={(porte) => patch({ porte })} />
              </div>
              <div className="pp-field">
                <label className="pp-lbl" htmlFor="pp-de">Aberta a partir de</label>
                <input className="pp-ctl" type="date" id="pp-de" value={filters.data_abertura_de} onChange={setField('data_abertura_de')} />
              </div>
              <div className="pp-field">
                <label className="pp-lbl" htmlFor="pp-ate">Aberta até</label>
                <input className="pp-ctl" type="date" id="pp-ate" value={filters.data_abertura_ate} onChange={setField('data_abertura_ate')} />
              </div>
              <div className="pp-field pp-span4">
                <label className="pp-switch">
                  <input type="checkbox" checked={filters.opcao_simples} onChange={setField('opcao_simples')} />
                  <span><strong>Simples Nacional</strong><small>Mostrar só empresas optantes</small></span>
                </label>
              </div>
            </div>
          </div>

          <div className="pp-panel-foot">
            <div className="pp-chips" aria-live="polite">
              {chips.length ? chips.map((c) => (
                <button key={c.key} type="button" className="pp-chip" aria-label={`Remover filtro ${c.label}`} onClick={() => patch(c.clear)}>
                  <span>{c.label}</span>
                  <span className="pp-chip-x"><Icon n="x" s={14} /></span>
                </button>
              )) : <span className="pp-chips-empty">Nenhum filtro aplicado</span>}
            </div>
            <div className="pp-actions">
              <button type="button" className="pp-btn pp-btn-ghost" disabled={!chips.length || loading} onClick={limpar}>Limpar filtros</button>
              <button type="submit" className="pp-btn pp-btn-primary" disabled={loading}>
                <Icon n="search" s={20} /> {loading ? 'Buscando…' : 'Buscar'}
              </button>
            </div>
          </div>
        </form>

        <section className="pp-results" aria-live="polite">
          {loading ? (
            <div className="pp-card">
              <div className="pp-res-head"><h3>Buscando…</h3></div>
              <div className="pp-skel"><div /><div /><div /></div>
            </div>
          ) : !result ? (
            <div className="pp-card">
              <div className="pp-empty">
                <svg viewBox="0 0 132 104" fill="none" aria-hidden="true">
                  <rect x="14" y="30" width="58" height="64" rx="8" fill="#eaf0ff" stroke="#2251e8" strokeWidth="2" />
                  <path d="M28 46h10M48 46h10M28 60h10M48 60h10M28 74h10M48 74h10" stroke="#2251e8" strokeWidth="3" strokeLinecap="round" />
                  <rect x="80" y="48" width="38" height="46" rx="6" fill="#fff" stroke="#ccd2e0" strokeWidth="2" />
                  <path d="M89 62h20M89 74h12" stroke="#ccd2e0" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="86" cy="26" r="16" fill="#fff" stroke="#0e1526" strokeWidth="2.5" />
                  <path d="m98 38 12 12" stroke="#0e1526" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                <h3>Encontre empresas para prospectar</h3>
                <p>Use o campo de busca e os filtros acima, ou comece por um destes atalhos.</p>
                <div className="pp-presets">
                  <button type="button" className="pp-preset" onClick={() => atalho({ data_abertura_de: isoDaysAgo(365), situacao_cadastral: 'ativa' })}>Abertas nos últimos 12 meses</button>
                  <button type="button" className="pp-preset" onClick={() => atalho({ uf: 'MG', situacao_cadastral: 'ativa' })}>Ativas em Minas Gerais</button>
                  <button type="button" className="pp-preset" onClick={() => atalho({ opcao_simples: true })}>Optantes do Simples</button>
                </div>
              </div>
            </div>
          ) : !items.length ? (
            <div className="pp-card">
              <div className="pp-empty">
                <h3>Nenhuma empresa encontrada</h3>
                <p>Nenhum resultado com esses filtros. Remova algum deles ou amplie o período de abertura.</p>
                <button type="button" className="pp-btn pp-btn-line pp-btn-sm" onClick={limpar}>Limpar filtros</button>
              </div>
            </div>
          ) : (
            <>
              <div className="pp-card">
                <div className="pp-res-head">
                  <h3>{resultTitle}</h3>
                  <button type="button" className="pp-btn pp-btn-primary pp-btn-sm" disabled={!selected.size || importing.size > 0} onClick={() => importar([...selected])}>
                    {selected.size ? `Importar selecionadas (${selected.size})` : 'Importar selecionadas'}
                  </button>
                </div>
                <div className="pp-tbl-wrap">
                  <table className="pp-table">
                    <thead>
                      <tr>
                        <th><input className="pp-tbl-check" type="checkbox" checked={todasSelecionadas} disabled={!importaveis.length} onChange={toggleTodas} aria-label="Selecionar todas" /></th>
                        <th>Empresa</th>
                        <th>CNPJ</th>
                        <th>Local</th>
                        <th>Atividade principal</th>
                        <th>Porte</th>
                        <th>Situação</th>
                        <th>Abertura</th>
                        <th><span className="pp-sr">Ação</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => (
                        <tr key={it.cnpj}>
                          <td>
                            <input className="pp-tbl-check" type="checkbox" disabled={it.already_imported}
                              checked={selected.has(it.cnpj)} onChange={() => toggle(it.cnpj)}
                              aria-label={`Selecionar ${it.nome_fantasia || it.razao_social}`} />
                          </td>
                          <td>
                            <div className="pp-co">
                              <strong>{it.nome_fantasia || it.razao_social}</strong>
                              {it.nome_fantasia && <span>{it.razao_social}</span>}
                            </div>
                          </td>
                          <td className="pp-num">{fmtCnpj(it.cnpj)}</td>
                          <td>{[it.cidade, it.uf].filter(Boolean).join(' · ') || '—'}</td>
                          <td className="pp-cnae">
                            <span className="pp-num">{it.cnae_principal ? maskCnae(it.cnae_principal) : '—'}</span>
                            {it.cnae_principal_descricao && <small>{it.cnae_principal_descricao}</small>}
                          </td>
                          <td>{PORTE_LABEL[it.porte] || '—'}</td>
                          <td>
                            {it.situacao_cadastral
                              ? <span className={`pp-badge pp-b-${it.situacao_cadastral}`}>{SITUACAO_LABEL[it.situacao_cadastral] || it.situacao_cadastral}</span>
                              : '—'}
                          </td>
                          <td className="pp-num">{fmtDate(it.data_abertura)}</td>
                          <td>
                            {it.already_imported ? (
                              <div className="pp-imported-actions">
                                <span className="pp-imported"><Icon n="check" s={16} /> Importada</span>
                                <button type="button" className="pp-btn pp-btn-line pp-btn-sm" disabled={!!openingDeal} onClick={() => criarNegocio(it)}>
                                  {openingDeal === it.cnpj ? 'Abrindo…' : 'Criar negócio'}
                                </button>
                              </div>
                            ) : (
                              <button type="button" className="pp-btn pp-btn-line pp-btn-sm" disabled={importing.has(it.cnpj)} onClick={() => importar([it.cnpj])}>
                                <Icon n="download" s={16} /> {importing.has(it.cnpj) ? 'Importando…' : 'Importar'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="pp-pager">
                  <span>
                    Página {page} de {totalPages}{result.total_capped ? '+' : ''}
                    {result.total_capped && ' · refine os filtros para ver todos'}
                  </span>
                  <div className="pp-pages">
                    <button type="button" className="pp-btn pp-btn-line pp-btn-sm" disabled={page <= 1} onClick={() => buscar(page - 1)} aria-label="Página anterior">
                      <Icon n="chevron_l" s={16} />
                    </button>
                    <button type="button" className="pp-btn pp-btn-line pp-btn-sm" disabled={page >= totalPages} onClick={() => buscar(page + 1)} aria-label="Próxima página">
                      <Icon n="chevron_r" s={16} />
                    </button>
                  </div>
                </div>
              </div>
              <p className="pp-note">Empresas importadas passam a ser dados próprios do CRM. A base pública da Receita Federal não é alterada.</p>
            </>
          )}
        </section>
      </div>
      {dealCompany && <DealModal
        initialCompany={dealCompany}
        onClose={() => setDealCompany(null)}
        onSaved={() => { setDealCompany(null); setImportOk(true); setImportMsg('Negócio salvo e vinculado à empresa.') }}
      />}
    </Layout>
  )
}
